import React, {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import ReactDOM from 'react-dom'

import { TCustomization } from 'types/GlobalCustomization'
import { TDashboard } from 'types/GlobalDashboard'
import { DashboardFilter } from 'types/GlobalDashboardFilter'
import { PeriodFilter } from 'types/GlobalWidget'

import { useTheme } from '@mui/material'
import { useTranslation } from 'language/translator'

import Backdrop from 'components_new/atoms/Backdrop'
import Button from 'components_new/atoms/Button'
import Box from 'components_new/atoms/Box'

import DashboardUserFilterBar from 'components_new/organisms/DashboardUserFilterBar'

import { getTheme, getWidgetMode } from 'themes'

export interface GenericWidgetFullSizeWrapperProps {
  children?: ReactNode
  customization?: TCustomization
  dashboard?: TDashboard
  dashboardFilter: DashboardFilter
  fullSize: boolean
  offset?: {
    right?: {
      maxWidth: number
      minWidth: number
      width: string
    }
  }
  periodFilter?: PeriodFilter | null
  setFullSize: React.Dispatch<React.SetStateAction<boolean>>
}

export const STANDARD_WIDGET_SCALE_FACTOR = 0.8

const GenericWidgetFullSizeWrapper = (
  props: GenericWidgetFullSizeWrapperProps
) => {
  const {
    children,
    customization,
    dashboard,
    dashboardFilter,
    fullSize,
    offset,
    periodFilter = null,
    setFullSize
  } = props

  const theme = useTheme()

  const widgetMode = getWidgetMode(customization)
  const widgetTheme = getTheme(widgetMode, customization)
  const { t } = useTranslation()

  // Animation state: 'idle' | 'expanding' | 'expanded' | 'collapsing'
  const [animState, setAnimState] = useState<
    'idle' | 'expanding' | 'expanded' | 'collapsing'
  >('idle')
  const originRectRef = useRef<DOMRect | null>(null)

  // True while the widget needs fullscreen CSS
  // (including during expand/collapse animations)
  const useFullSizeLayout = animState !== 'idle'

  const contentRef = useRef<HTMLDivElement>(null)
  const inlineSlotRef = useRef<HTMLDivElement>(null)

  const handleCloseFullSize = useCallback(() => {
    setAnimState('collapsing')
    setFullSize(false)
  }, [])

  // Close fullscreen on Escape key
  useEffect(() => {
    if (!fullSize) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseFullSize()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [fullSize, handleCloseFullSize])

  // Move the content DOM node to #root (not document.body) to escape the
  // grid item's CSS transform that breaks position:fixed, while keeping
  // React event delegation working (events bubble through #root).
  const reactRootRef = useRef(document.getElementById('root'))

  useLayoutEffect(() => {
    const node = contentRef.current
    const reactRoot = reactRootRef.current

    if (!node || !reactRoot) {
      return
    }

    if (animState === 'expanding') {
      // Move to root and kick off expand animation
      reactRoot.appendChild(node)
      requestAnimationFrame(() => {
        setAnimState('expanded')
      })
    } else if (animState === 'idle') {
      // Move back to inline slot (after collapse finishes)
      if (inlineSlotRef.current && node.parentNode !== inlineSlotRef.current) {
        inlineSlotRef.current.appendChild(node)
      }
    }

    // No cleanup here — node must stay in #root during 'expanded' and
    // 'collapsing' states. Unmount cleanup is handled separately below.
  }, [animState])

  // Capture origin rect and start expand animation when fullSize becomes true.
  // useLayoutEffect runs before paint, so contentRef is still in its normal
  // position (animState is still 'idle', useFullSizeLayout is still false).
  useLayoutEffect(() => {
    if (fullSize) {
      if (animState === 'idle') {
        if (contentRef.current) {
          originRectRef.current = contentRef.current.getBoundingClientRect()
        }
        setAnimState('expanding')
      }
    } else if (animState === 'expanded') {
      // Parent deactivated fullSize without the close button — start collapse
      setAnimState('collapsing')
    }
  }, [fullSize])

  // Move node back to inline slot on component unmount
  useEffect(() => {
    return () => {
      const node = contentRef.current
      const reactRoot = reactRootRef.current

      if (
        node &&
        reactRoot &&
        node.parentNode === reactRoot &&
        inlineSlotRef.current
      ) {
        inlineSlotRef.current.appendChild(node)
      }
    }
  }, [])

  // Handle collapse transition end — signal that animation is done.
  // Only react to transitions on the content div itself (not bubbled from children).
  const handleTransitionEnd = useCallback(
    (event: React.TransitionEvent<HTMLDivElement>) => {
      if (event.target !== event.currentTarget) {
        return
      }

      // Wait for the slowest transition (height at 300ms) so the
      // early-finishing opacity transition (150ms) doesn't cut the position
      // animation short.
      if (event.propertyName !== 'height') {
        return
      }

      if (animState === 'collapsing') {
        setAnimState('idle')
      }
    },
    [animState]
  )

  const portalTarget = reactRootRef.current ?? document.body

  const showFilterBar =
    fullSize &&
    (dashboardFilter.length > 0 || Boolean(periodFilter)) &&
    dashboard

  return (
    <>
      <div ref={inlineSlotRef} style={{ height: '100%' }}>
        <div
          ref={contentRef}
          style={
            useFullSizeLayout
              ? {
                  position: 'fixed',
                  ...((animState === 'expanding' ||
                    animState === 'collapsing') &&
                  originRectRef.current
                    ? {
                        top: originRectRef.current.top,
                        left: originRectRef.current.left,
                        width: originRectRef.current.width,
                        height: originRectRef.current.height
                      }
                    : {
                        top: 16,
                        left: 16,
                        width: `calc(100vw - 32px - ${
                          offset?.right ? `${offset.right.width}` : '0px'
                        })`,
                        height: 'calc(100vh - 32px)',
                        ...(offset?.right
                          ? {
                              maxWidth: `calc(100vw - 32px - ${offset.right.minWidth}px)`,
                              minWidth: `calc(100vw - 32px - ${offset.right.maxWidth}px)`
                            }
                          : null)
                      }),
                  zIndex: theme.zIndex.modal - 1,
                  background:
                    customization?.background_colors?.paper ||
                    widgetTheme.palette.background.paper,
                  borderRadius: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  padding: 16,
                  minWidth: 0,
                  overflow: 'auto',
                  opacity:
                    animState === 'expanding' || animState === 'collapsing'
                      ? 0
                      : 1,
                  transition: `top 300ms ease, left 300ms ease, width 300ms ease, height 300ms ease${
                    animState !== 'expanding' ? ', opacity 150ms ease' : ''
                  }`
                }
              : {
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                  position: 'relative',
                  overflow: 'hidden'
                }
          }
          onMouseDown={
            useFullSizeLayout ? (event) => event.stopPropagation() : undefined
          }
          onTransitionEnd={handleTransitionEnd}
        >
          {showFilterBar ? (
            <DashboardUserFilterBar
              dashboard={dashboard}
              sx={{ border: 'none', p: 0, mb: 1 }}
            />
          ) : null}

          {children}

          {fullSize ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="text" onClick={handleCloseFullSize}>
                {t('global', 'close')}
              </Button>
            </Box>
          ) : null}
        </div>
      </div>

      {/*-- backdrop (portaled to body to escape grid transform) --*/}
      {fullSize
        ? ReactDOM.createPortal(
          <Backdrop
            open={true}
            onClick={handleCloseFullSize}
            sx={{ zIndex: theme.zIndex.modal - 2 }}
          />,
          portalTarget
        )
        : null}
    </>
  )
}

export default GenericWidgetFullSizeWrapper
